import { supabase } from '@/lib/supabase';
import type { Profile, ProfileUpdate } from '@/lib/database.types';

/** Ελάχιστο, ασφαλές υποσύνολο του προφίλ που επιστρέφουν τα search_users/
 * suggest_users RPC — ΠΟΤΕ ολόκληρη η γραμμή (τηλέφωνο, wallet, υπογραφή,
 * IBAN κτλ ΔΕΝ εκτίθενται σε αποτελέσματα αναζήτησης). */
export type SearchProfileResult = {
  id: string;
  full_name: string | null;
  nickname: string | null;
  avatar_url: string | null;
  verified: boolean;
  user_number: number | null;
  current_level: number;
  business_name: string | null;
  show_business_name: boolean;
  business_logo_url: string | null;
  use_logo_as_avatar: boolean;
  portfolio_enabled: boolean;
  portfolio_public: boolean;
  bio: string | null;
  specialty: string | null;
  contact_email?: string | null;
};

/** Φέρνει το πλήρες προφίλ του τρέχοντος χρήστη (συμπεριλαμβανομένων των ευαίσθητων πεδίων). */
export async function getMyProfile(): Promise<Profile | null> {
  const { data, error } = await supabase.rpc('get_my_profile');
  if (error) throw error;
  return (data as Profile) ?? null;
}

/**
 * Φέρνει το δημόσιο προφίλ ενός άλλου χρήστη μέσω get_profiles_public — η
 * function (πρώην profiles_public view) γεμίζει με NULL phone/signature_text/svg,
 * wallet_balance/escrow_ink, business_phone/business_email εκτός αν ο καλών
 * όντως μοιράζεται συμφωνία/προσφορά με αυτό το άτομο, και γεμίζει με NULL τα
 * στοιχεία πληρωμής (IBAN/IRIS/Revolut/Viva) εκτός αν ισχύει αυτή η σχέση Ή το
 * προφίλ έχει δημόσιο επαγγελματικό portfolio (το σενάριο "πλήρωσε αυτόν τον
 * επαγγελματία").
 */
export async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase.rpc('get_profiles_public', { p_ids: [userId] });
  if (error) throw error;
  return (data?.[0] as unknown as Profile) ?? null;
}

/**
 * Ενημερώνει (ή δημιουργεί αν λείπει) το προφίλ του χρήστη και επιστρέφει
 * την νέα εγγραφή. Χρησιμοποιεί upsert ώστε να δουλεύει ακόμη κι αν το row
 * δεν έχει προλάβει να δημιουργηθεί από το trigger της εγγραφής.
 */
export async function updateProfile(userId: string, patch: ProfileUpdate): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .upsert({ id: userId, ...patch })
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

/** Το προφίλ θεωρείται «πλήρες» όταν έχει συμπληρωμένο ονοματεπώνυμο. */
export function isProfileComplete(profile: Profile | null): boolean {
  return !!profile?.full_name?.trim();
}

/**
 * Αντικαθιστά οπτικά παρόμοια ελληνικά γράμματα με λατινικά.
 * Χρήσιμο όταν ο χρήστης έχει λάθος γλώσσα πληκτρολογίου (π.χ. ΗΕΙ → HEI).
 */
const GREEK_TO_LATIN: Record<string, string> = {
  'Α': 'A', 'α': 'a',
  'Β': 'B', 'β': 'B',
  'Ε': 'E', 'ε': 'E',
  'Ζ': 'Z', 'ζ': 'Z',
  'Η': 'H', 'η': 'H',
  'Ι': 'I', 'ι': 'I',
  'Κ': 'K', 'κ': 'K',
  'Μ': 'M', 'μ': 'M',
  'Ν': 'N', 'ν': 'N',
  'Ο': 'O', 'ο': 'O',
  'Ρ': 'P', 'ρ': 'p',
  'Τ': 'T', 'τ': 'T',
  'Υ': 'Y', 'υ': 'Y',
  'Χ': 'X', 'χ': 'X',
};

/** Βρίσκει προφίλ με βάση το user_number (π.χ. HEI-00003 → 3). */
export async function fetchProfileByUserNumber(n: number): Promise<Profile | null> {
  const { data, error } = await supabase.rpc('get_profile_public_by_user_number', { p_user_number: n });
  if (error) throw error;
  return (data?.[0] as unknown as Profile) ?? null;
}

export function latinizeQuery(s: string): string {
  return s.split('').map((c) => GREEK_TO_LATIN[c] ?? c).join('');
}

const LATIN_TO_GREEK: Record<string, string> = {
  'a': 'α', 'A': 'α',
  'b': 'β', 'B': 'β',
  'g': 'γ', 'G': 'γ',
  'd': 'δ', 'D': 'δ',
  'e': 'ε', 'E': 'ε',
  'z': 'ζ', 'Z': 'ζ',
  'i': 'ι', 'I': 'ι',
  'k': 'κ', 'K': 'κ',
  'l': 'λ', 'L': 'λ',
  'm': 'μ', 'M': 'μ',
  'n': 'ν', 'N': 'ν',
  'o': 'ο', 'O': 'ο',
  'p': 'π', 'P': 'π',
  'r': 'ρ', 'R': 'ρ',
  's': 'σ', 'S': 'σ',
  't': 'τ', 'T': 'τ',
  'u': 'υ', 'U': 'υ',
  'f': 'φ', 'F': 'φ',
  'x': 'χ', 'X': 'χ',
  'y': 'υ', 'Y': 'υ',
  'v': 'β', 'V': 'β',
  'h': 'η', 'H': 'η',
};

// Digraphs must be checked before single chars — order matters.
const DIGRAPHS: [string, string][] = [
  ['th', 'θ'], ['TH', 'θ'], ['Th', 'θ'],
  ['ch', 'χ'], ['CH', 'χ'], ['Ch', 'χ'],
  ['ph', 'φ'], ['PH', 'φ'], ['Ph', 'φ'],
  ['ps', 'ψ'], ['PS', 'ψ'], ['Ps', 'ψ'],
  ['ks', 'ξ'], ['KS', 'ξ'], ['Ks', 'ξ'],
  ['gg', 'γγ'], ['GG', 'γγ'],
  ['gk', 'γκ'], ['GK', 'γκ'], ['Gk', 'γκ'],
  ['mp', 'μπ'], ['MP', 'μπ'], ['Mp', 'μπ'],
  ['nt', 'ντ'], ['NT', 'ντ'], ['Nt', 'ντ'],
  ['ou', 'ου'], ['OU', 'ου'], ['Ou', 'ου'],
  ['au', 'αυ'], ['AU', 'αυ'], ['Au', 'αυ'],
  ['eu', 'ευ'], ['EU', 'ευ'], ['Eu', 'ευ'],
  ['ai', 'αι'], ['AI', 'αι'], ['Ai', 'αι'],
  ['ei', 'ει'], ['EI', 'ει'], ['Ei', 'ει'],
  ['oi', 'οι'], ['OI', 'οι'], ['Oi', 'οι'],
];

/** Μετατρέπει greeklish σε φωνητικά ελληνικά — "aggelos" → "αγγελοσ", "thanasis" → "θαναση". */
export function phoneticGreekQuery(s: string): string {
  let result = s;
  for (const [from, to] of DIGRAPHS) {
    result = result.split(from).join(to);
  }
  return result.split('').map((c) => LATIN_TO_GREEK[c] ?? c).join('');
}

/**
 * Αναζήτηση με όνομα: δοκιμάζει το αρχικό query + phonetic Greek εκδοχή.
 * Χρησιμοποιείται για αναζήτηση αντισυμβαλλόμενου σε νέα συμφωνία.
 */
export async function searchProfilesByName(query: string, excludeId?: string): Promise<SearchProfileResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const latinized = latinizeQuery(q);
  const greekified = phoneticGreekQuery(q);
  const queries = [q];
  if (latinized !== q) queries.push(latinized);
  if (greekified !== q) queries.push(greekified);
  const results = await Promise.all(queries.map((qv) => rpcSearch('search_users', qv, excludeId)));
  return mergeUnique(results[0] ?? [], results.slice(1).flat());
}

async function rpcSearch(rpc: 'search_users' | 'suggest_users', q: string, excludeId?: string): Promise<SearchProfileResult[]> {
  const { data, error } = await supabase.rpc(rpc, { q, exclude_id: excludeId });
  if (error) throw error;
  return data ?? [];
}

function mergeUnique(a: SearchProfileResult[], b: SearchProfileResult[]): SearchProfileResult[] {
  const seen = new Set(a.map((p) => p.id));
  return [...a, ...b.filter((p) => !seen.has(p.id))];
}

/**
 * Αναζήτηση χρηστών: αγνοεί τόνους & κεφαλαία, μερική αντιστοίχιση,
 * δέχεται UserID (π.χ. "HEI-00123" ή "123"), και χειρίζεται λάθος
 * γλώσσα πληκτρολογίου (ΗΕΙ-00001 → HEI-00001). ≥2 χαρακτήρες.
 */
export async function searchProfiles(query: string, excludeId?: string): Promise<SearchProfileResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const latinized = latinizeQuery(q);
  if (latinized === q) {
    return rpcSearch('search_users', q, excludeId);
  }
  const [original, latin] = await Promise.all([
    rpcSearch('search_users', q, excludeId),
    rpcSearch('search_users', latinized, excludeId),
  ]);
  return mergeUnique(original, latin);
}

/**
 * Προτάσεις «μήπως εννοείτε» (fuzzy, pg_trgm) — επίσης με transliteration.
 */
export async function suggestProfiles(query: string, excludeId?: string): Promise<SearchProfileResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const latinized = latinizeQuery(q);
  if (latinized === q) {
    return rpcSearch('suggest_users', q, excludeId);
  }
  const [original, latin] = await Promise.all([
    rpcSearch('suggest_users', q, excludeId),
    rpcSearch('suggest_users', latinized, excludeId),
  ]);
  return mergeUnique(original, latin);
}
